import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthContext } from './components/AuthProvider';
import AuthModal from './components/AuthModal';
import OnboardingQuestionnaireSlides from './components/OnboardingQuestionnaireSlides';
import { getOnboardingStatus, getProject } from './lib/api';
import './OnboardingPage.css';

const PENDING_SUBMIT_STORAGE_KEY = 'pendingSubmit';

function OnboardingPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, openAuthModal, authModalOpen, closeAuthModal, signInWithGoogle, signInWithEmail } = useAuthContext();

  const userId = user?.id || null;
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);

  const [statusText] = useState('Starting your project…');

  const pendingFromStorage = useMemo(() => {
    try {
      const raw =
        sessionStorage.getItem(PENDING_SUBMIT_STORAGE_KEY) ||
        localStorage.getItem(PENDING_SUBMIT_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }, []);

  // Prefer navigation state for non-OAuth flows (authenticated submit with file objects).
  const statePromptText = location.state?.promptText ?? null;
  const stateAttachments = location.state?.attachments ?? null;

  const promptText = statePromptText ?? pendingFromStorage?.promptText ?? '';
  const attachments = stateAttachments ?? pendingFromStorage?.attachments ?? [];

  useEffect(() => {
    // If user isn't authenticated yet, prompt login.
    if (loading) return;
    if (!user && !authModalOpen) openAuthModal();
  }, [authModalOpen, loading, openAuthModal, user]);

  useEffect(() => {
    if (!userId || !projectId) return;
    let cancelled = false;
    setIsCheckingOnboarding(true);
    (async () => {
      try {
        const res = await getOnboardingStatus();
        if (cancelled) return;
        const completed = !!res?.completed;
        setOnboardingCompleted(completed);
        if (completed) {
          navigate(`/project/${projectId}`, { replace: true });
        }
      } catch {
        // If status check fails, default to showing onboarding (safe).
      } finally {
        if (!cancelled) setIsCheckingOnboarding(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, projectId, userId]);

  // Project generation is started from LandingPage / authenticated submit flow.
  // Onboarding page only collects answers and then returns the user to the project.

  const handleDone = async () => {
    // After finishing (or skipping) onboarding, check project status.
    // If project is completed, go to the editor. Otherwise, go to projects page.
    try {
      const project = await getProject(projectId);
      if (project?.status === 'completed') {
        navigate(`/project/${projectId}`, {
          state: {
            promptText,
            attachments,
          },
        });
      } else {
        // Project is still processing, go to projects page with highlight
        navigate('/projects', { state: { highlightProjectId: projectId } });
      }
    } catch {
      // If we can't fetch the project, default to projects page with highlight
      navigate('/projects', { state: { highlightProjectId: projectId } });
    }
  };

  return (
    <div className="onboarding-page-root">
      <div className="onboarding-page-content">
        {userId && !onboardingCompleted ? (
          <OnboardingQuestionnaireSlides userId={userId} onDone={handleDone} />
        ) : (
          <div className="onboarding-page-waiting">
            {userId ? (isCheckingOnboarding ? 'Loading…' : statusText) : 'Please sign in to continue.'}
          </div>
        )}
      </div>

      <AuthModal
        isOpen={authModalOpen}
        onClose={closeAuthModal}
        onSignIn={() => signInWithGoogle()}
        onEmailSignIn={signInWithEmail}
      />
    </div>
  );
}

export default OnboardingPage;


